import { useState, useEffect } from "react";

import { App, PostMessageTransport } from "@modelcontextprotocol/ext-apps";

export type OpenAiGlobals<
    ToolInput = UnknownObject,
    ToolOutput = UnknownObject,
    ToolResponseMetadata = UnknownObject,
    WidgetState = UnknownObject
> = {
    theme: Theme;

    userAgent: UserAgent;
    locale: string;

    maxHeight: number;
    displayMode: DisplayMode;
    safeArea: SafeArea;

    toolInput: ToolInput;
    toolOutput: ToolOutput | null;
    toolResponseMetadata: ToolResponseMetadata | null;
    widgetState: WidgetState | null;
    setWidgetState: (state: WidgetState) => Promise<void>;
};

type API = {
    callTool: CallTool;
    sendFollowUpMessage: (args: { prompt: string }) => Promise<void>;
    openExternal(payload: { href: string }): void;

    requestDisplayMode: RequestDisplayMode;
};

export type UnknownObject = Record<string, unknown>;

export type Theme = "light" | "dark";

export type SafeAreaInsets = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

export type SafeArea = {
    insets: SafeAreaInsets;
};

export type DeviceType = "mobile" | "tablet" | "desktop" | "unknown";

export type UserAgent = {
    device: { type: DeviceType };
    capabilities: {
        hover: boolean;
        touch: boolean;
    };
};

export type DisplayMode = "pip" | "inline" | "fullscreen";
export type RequestDisplayMode = (args: { mode: DisplayMode }) => Promise<{
    mode: DisplayMode;
}>;

export type CallToolResponse = {
    content?: Array<Record<string, unknown>>;
    structuredContent?: Record<string, unknown>;
    isError?: boolean;
    [key: string]: unknown;
};

export type CallTool = (
    name: string,
    args: Record<string, unknown>
) => Promise<CallToolResponse>;

export const SET_GLOBALS_EVENT_TYPE = "openai:set_globals";
export class SetGlobalsEvent extends CustomEvent<{
    globals: Partial<OpenAiGlobals>;
}> {
    readonly type = SET_GLOBALS_EVENT_TYPE;
}

declare global {
    interface Window {
        openai: API & OpenAiGlobals;
    }

    interface WindowEventMap {
        [SET_GLOBALS_EVENT_TYPE]: SetGlobalsEvent;
    }
}

// Claude and other MCP Apps hosts don't inject `window.openai`. When embedded
// there instead, the functions below bridge to the same host capabilities over
// the MCP Apps postMessage protocol via `@modelcontextprotocol/ext-apps`.
// See https://modelcontextprotocol.io/extensions/apps/overview
function isChatGptHost(): boolean {
    return typeof window !== "undefined" && !!window.openai;
}

function isEmbedded(): boolean {
    return typeof window !== "undefined" && window.parent !== window;
}

type ToolResultListener = (structuredContent: UnknownObject | null) => void;
type ToolResponseMetadataListener = (metadata: UnknownObject | null) => void;
type DisplayModeListener = (displayMode: DisplayMode) => void;

// The player fills its container based on the viewport (fixed/absolute
// positioning), not document flow, so it has no natural content height for
// the host to auto-measure. Report a fixed inline height instead of relying
// on ResizeObserver-based auto-resize, which otherwise settles on the tiny
// default size the host starts the iframe at.
const WIDGET_HEIGHT_PX = 480;

// Claude renders "fullscreen" display mode as an iframe at height: 100% and
// ignores sendSizeChanged/autoResize there, which lets the player render
// underneath Claude's floating chat composer. There's no supported way to
// resize the iframe itself from inside it in this mode, so instead we shrink
// our own content to leave room at the bottom (see the matching CSS rule
// generated in cloudrun/openai-mcp/src/handlers/presentationWidget.ts).
export const MCP_APP_FULLSCREEN_CLASS = "mcp-app-fullscreen";

let mcpAppConnectPromise: Promise<App> | null = null;
let latestToolOutput: UnknownObject | null = null;
let latestToolResponseMetadata: UnknownObject | null = null;
let latestDisplayMode: DisplayMode = "inline";
const toolResultListeners = new Set<ToolResultListener>();
const toolResponseMetadataListeners = new Set<ToolResponseMetadataListener>();
const displayModeListeners = new Set<DisplayModeListener>();

function applyFullscreenClass(displayMode: DisplayMode) {
    if (typeof document === "undefined") {
        return;
    }

    document.documentElement.classList.toggle(MCP_APP_FULLSCREEN_CLASS, displayMode === "fullscreen");
}

function setLatestDisplayMode(displayMode: DisplayMode) {
    latestDisplayMode = displayMode;
    applyFullscreenClass(displayMode);
    displayModeListeners.forEach(listener => listener(displayMode));
}

function getMcpApp(): Promise<App> {
    if (!mcpAppConnectPromise) {
        const app = new App(
            { name: "beautifulai-presentation-widget", version: "1.0.0" },
            { availableDisplayModes: ["inline", "fullscreen"] },
            { autoResize: false }
        );

        // Register listeners before connect(): the host can send the initial
        // tool result immediately after the handshake, and registering a
        // listener afterwards can miss it, leaving the iframe stuck hidden.
        // https://github.com/anthropics/claude-ai-mcp/issues/61
        app.addEventListener("toolresult", params => {
            latestToolOutput = (params.structuredContent as UnknownObject | undefined) ?? null;
            latestToolResponseMetadata = (params._meta as UnknownObject | undefined) ?? null;
            toolResultListeners.forEach(listener => listener(latestToolOutput));
            toolResponseMetadataListeners.forEach(listener => listener(latestToolResponseMetadata));
        });
        app.addEventListener("hostcontextchanged", ctx => {
            if (ctx.displayMode) {
                setLatestDisplayMode(ctx.displayMode);
            }
        });

        mcpAppConnectPromise = app
            .connect(new PostMessageTransport(window.parent, window.parent))
            .then(() => {
                setLatestDisplayMode(app.getHostContext()?.displayMode ?? latestDisplayMode);
                app.sendSizeChanged({ height: WIDGET_HEIGHT_PX })
                    .catch(err => console.error("[openaiApp] Failed to report widget size", err));
                return app;
            });
    }

    return mcpAppConnectPromise;
}

export function useOpenAiGlobal<K extends keyof OpenAiGlobals>(
    key: K
): OpenAiGlobals[K] | null {
    const [value, setValue] = useState<OpenAiGlobals[K] | null>(null);

    useEffect(() => {
        if (isChatGptHost()) {
            if (window.openai[key]) {
                setValue(window.openai[key]);
            }

            const handleSetGlobal = (event: SetGlobalsEvent) => {
                const value = event.detail.globals[key];
                if (value === undefined) {
                    return;
                }

                setValue(value);
            };

            window.addEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal, {
                passive: true,
            });

            return () => {
                window.removeEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal);
            };
        }

        // toolOutput, toolResponseMetadata, and displayMode are the only globals this app reads. Other hosts
        // (e.g. Claude's MCP Apps) don't need the rest bridged.
        if (key === "toolOutput" && isEmbedded()) {
            const listener: ToolResultListener = structuredContent => {
                setValue(structuredContent as OpenAiGlobals[K] | null);
            };

            toolResultListeners.add(listener);
            if (latestToolOutput) {
                listener(latestToolOutput);
            }

            getMcpApp().catch(err => console.error("[openaiApp] Failed to connect MCP App bridge", err));

            return () => {
                toolResultListeners.delete(listener);
            };
        }

        if (key === "toolResponseMetadata" && isEmbedded()) {
            const listener: ToolResponseMetadataListener = metadata => {
                setValue(metadata as OpenAiGlobals[K] | null);
            };

            toolResponseMetadataListeners.add(listener);
            if (latestToolResponseMetadata) {
                listener(latestToolResponseMetadata);
            }

            getMcpApp().catch(err => console.error("[openaiApp] Failed to connect MCP App bridge", err));

            return () => {
                toolResponseMetadataListeners.delete(listener);
            };
        }

        if (key === "displayMode" && isEmbedded()) {
            const listener: DisplayModeListener = displayMode => {
                setValue(displayMode as OpenAiGlobals[K]);
            };

            displayModeListeners.add(listener);
            listener(latestDisplayMode);

            getMcpApp().catch(err => console.error("[openaiApp] Failed to connect MCP App bridge", err));

            return () => {
                displayModeListeners.delete(listener);
            };
        }

        return undefined;
    }, []);

    return value;
}

export function useDisplayMode(): DisplayMode {
    return useOpenAiGlobal("displayMode") ?? "inline";
}

export function isFullscreen() {
    if (isChatGptHost()) {
        return window.openai.displayMode === "fullscreen";
    }

    return latestDisplayMode === "fullscreen";
}

export async function enterFullscreen(): Promise<{ mode: DisplayMode } | undefined> {
    try {
        if (isChatGptHost()) {
            return await window.openai.requestDisplayMode({ mode: "fullscreen" });
        }

        if (isEmbedded()) {
            const app = await getMcpApp();
            const hostContext = app.getHostContext();
            if (!hostContext?.availableDisplayModes?.includes("fullscreen")) {
                return { mode: hostContext?.displayMode ?? latestDisplayMode };
            }
            return await app.requestDisplayMode({ mode: "fullscreen" });
        }
    } catch (err) {
        console.error("[openaiApp] Failed to request fullscreen", err);
    }

    return undefined;
}

export function exitFullscreen() {
    if (isChatGptHost()) {
        window.openai.requestDisplayMode({ mode: "inline" });
        return;
    }

    if (isEmbedded()) {
        getMcpApp()
            .then(app => app.requestDisplayMode({ mode: "inline" }))
            .catch(err => console.error("[openaiApp] Failed to exit fullscreen", err));
    }
}

export function openExternal(href: string) {
    if (isChatGptHost()) {
        window.openai.openExternal({ href });
        return;
    }

    if (isEmbedded()) {
        getMcpApp()
            .then(app => app.openLink({ url: href }))
            .catch(err => console.error("[openaiApp] Failed to open external link", err));
    }
}
